import { RuleId } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { BiCtaNames } from '../../bi/types';
import { convertBooleanStatusToElementStatus, convertScanStatusToUiStatus } from '../../components/fixers/alt-image-fixer/utils';
import { MediaAlternativesRuleFixer } from '../../components/fixers/media-alternatives-fixer/media-alternatives-rule-fixer';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getAxeBreakpointId } from '../../utils/get-axe-breakpoint-id';
import { getUpdatedMediaAlternativesFromState } from '../rule-handlers/media-alternatives/get-updated-media-alternatives-from-state';
import { useBaseMediaAlternativesHandlers } from '../rule-handlers/media-alternatives/use-base-media-alternatives-handlers';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
export const useMediaAlternativesRule = () => {
  const {
    t
  } = useTranslation();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const biLogger = useBiLogger();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const ruleHandlers = useBaseMediaAlternativesHandlers();
  const {
    getRuleState,
    panelState,
    getPagesData,
    getGroupData,
    setRuleState
  } = usePanelStateAtom();
  const pageIdToPageData = getPagesData();
  const getMediaAlternativesProps = useCallback((ruleExecutionResult, pageId) => {
    const failedElementIdsSet = new Set();
    const mediaAlternativesProps = [];
    ruleExecutionResult.forEach(_ref => {
      var _pageIdToPageData$get;
      let {
        result,
        context,
        metadata
      } = _ref;
      const {
        failedElements
      } = result;
      const innerRoutes = (_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null ? void 0 : _pageIdToPageData$get.innerRoutes;
      if (innerRoutes === undefined || innerRoutes.find(route => (metadata == null ? void 0 : metadata.pageId) === route.id) !== undefined || (metadata == null ? void 0 : metadata.pageId) === pageId) {
        context.allMedia.forEach(_ref2 => {
          let {
            id,
            type,
            tpaData,
            title,
            uri,
            sourceType
          } = _ref2;
          if (!failedElementIdsSet.has(id)) {
            failedElementIdsSet.add(id);
            mediaAlternativesProps.push({
              breakpointId: getAxeBreakpointId(a11yWizardAdapter, editorHost, metadata, (tpaData == null ? void 0 : tpaData.componentId) ?? id),
              entityId: (metadata == null ? void 0 : metadata.pageId) ?? '',
              id,
              sourceType,
              status: convertBooleanStatusToElementStatus(failedElements.find(failedElement => failedElement === id) === undefined),
              title: title ?? '',
              tpaData,
              type,
              uri
            });
          }
        });
      }
    });
    return mediaAlternativesProps;
  }, [a11yWizardAdapter, editorHost, pageIdToPageData]);
  const getMediaAlternatives = useCallback(_ref3 => {
    var _ruleState$state, _getGroupData;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref3;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const ruleId = RuleId.MEDIA_ALTERNATIVES;
    const ruleState = getRuleState(routerId ?? pageId, ruleId);
    const isNotApplicable = ruleHandlers.getIsNotApplicable(ruleExecutionResult);
    if (isNotApplicable) {
      return null;
    }
    const status = ruleHandlers.getRuleStatus(ruleExecutionResult);
    const baseMediaProps = getMediaAlternativesProps(ruleExecutionResult, pageId);
    const mediaProps = getUpdatedMediaAlternativesFromState(baseMediaProps, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedMedia, panelState.globalSectionComponentsDataMap);
    const uiStatus = mediaProps.some(rule => rule.status === 'failed') ? RuleListStatus.failed : RuleListStatus.passed;
    const elementCount = mediaProps.length;
    const failedElementCount = mediaProps.filter(rule => !convertElementStatusToBooleanStatus(rule.status)).length;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const analysisState = (_getGroupData = getGroupData(pageId)) == null ? void 0 : _getGroupData.analysisState;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, ruleId, {
        status: uiStatus
      });
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: status === 'passed' ? 'accessibility-wizard.rules-list.media-alternatives.description' : 'accessibility-wizard.rules-list.media-alternatives.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-media-alternatives-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        },
        title: uiStatus === 'passed' ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount,
      failedElementCount,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: /*#__PURE__*/React.createElement(MediaAlternativesRuleFixer, {
          mediaRules: mediaProps,
          openMediaElement: ruleHandlers.openMediaElement,
          pageId: routerId ?? pageId,
          setMediaAlternatives: ruleHandlers.setMediaAlternatives
        })
      },
      id: `${routerId ?? pageId}-${ruleId}`,
      label: t('accessibility-wizard.rules-list.media-alternatives.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        var _ruleExecutionResult$;
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: convertScanStatusToUiStatus(status),
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(ruleExecutionResult == null || (_ruleExecutionResult$ = ruleExecutionResult[0]) == null || (_ruleExecutionResult$ = _ruleExecutionResult$.metadata) == null ? void 0 : _ruleExecutionResult$.itemType, isCrossPage),
            status: convertScanStatusToUiStatus(status),
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: convertScanStatusToUiStatus(status)
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          status: convertScanStatusToUiStatus(status)
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [getRuleState, ruleHandlers, getMediaAlternativesProps, panelState.globalSectionComponentsDataMap, getGroupData, t, setRuleState, openSelectedArticle, editorHost, biLogger, setCurrentTaskInfo, currentTaskInfo, navigateToPage]);
  return {
    getMediaAlternatives
  };
};
//# sourceMappingURL=use-media-alternatives-rule.js.map